import { CheckIcon, FolderIcon, HistoryIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "utils";
import { Avatar, AvatarFallback } from "components/ui/avatar";
import { Text } from "components/text";
import { PickerProject } from "./AddToProjectPicker.types";

interface ProjectRowProps {
  project: PickerProject;
  leadingIcon?: "folder" | "history";
  highlightQuery?: string;
  selectionDisabled?: boolean;
  assignedSelectionEnabled?: boolean;
  onSelect: (project: PickerProject) => void;
}

const renderName = (name: string, query?: string) => {
  const normalised = query?.trim().toLowerCase();
  if (!normalised) {
    return name;
  }
  const index = name.toLowerCase().indexOf(normalised);
  if (index === -1) {
    return name;
  }
  const before = name.slice(0, index);
  const match = name.slice(index, index + normalised.length);
  const after = name.slice(index + normalised.length);
  return (
    <>
      {before}
      <Text as="span" variant="title">
        {match}
      </Text>
      {after}
    </>
  );
};

export const ProjectRow = ({
  project,
  leadingIcon = "folder",
  highlightQuery,
  selectionDisabled = false,
  assignedSelectionEnabled = false,
  onSelect,
}: ProjectRowProps) => {
  const { t } = useTranslation(["components"]);
  const LeadingIcon = leadingIcon === "history" ? HistoryIcon : FolderIcon;
  const isAssigned = project.assigned;
  const isDisabled =
    selectionDisabled || (isAssigned && !assignedSelectionEnabled);
  const ariaLabel = project.assigned
    ? t("components:addToProjectPicker.assignedProject", {
        name: project.name,
      })
    : project.name;

  return (
    <button
      type="button"
      disabled={isDisabled}
      onClick={() => onSelect(project)}
      aria-label={ariaLabel}
      aria-disabled={isDisabled}
      className={cn(
        "group flex w-full items-center gap-3 rounded-sm px-2 py-2.5 text-sm outline-hidden select-none",
        isDisabled
          ? "cursor-default opacity-60"
          : "hover:bg-accent focus-visible:bg-accent",
        isAssigned && !isDisabled ? "opacity-60" : undefined,
      )}
    >
      <LeadingIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="flex-1 truncate text-left">
        {renderName(project.name, highlightQuery)}
      </span>
      <span className="flex shrink-0 items-center gap-2">
        {project.isShared && project.ownerInitials ? (
          <Avatar className="h-6 w-6">
            <AvatarFallback className="text-[10px]">
              {project.ownerInitials}
            </AvatarFallback>
          </Avatar>
        ) : null}
        {project.assigned ? (
          <CheckIcon className="h-4 w-4 text-primary-900" aria-hidden />
        ) : null}
      </span>
    </button>
  );
};
